import { useState, useEffect, useMemo } from 'react';
import { getStatusFromWeeklyShifts } from '../../helpers/dashboard/dashboardHelpers';
import { SHIFT_STATUS, EARLY_CLOCK_IN_WINDOW } from '../../constants/shiftConstants';

export const useShiftStatus = (employeeShifts) => {
  const [showStartButton, setShowStartButton] = useState(false);
  const [showEndButton, setShowEndButton] = useState(false);
  const [showBreakButton, setShowBreakButton] = useState(false);
  const [showEndMealBreakButton, setShowEndMealBreakButton] = useState(false);
  const [showRestButton, setShowRestButton] = useState(false);
  const [showEndRestBreakButton, setShowEndRestBreakButton] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [currentBreak, setCurrentBreak] = useState(null);

  // Get the current status from the shifts (memoized)
  const { status, relevantShift } = useMemo(() => {
    const result = getStatusFromWeeklyShifts(employeeShifts) || {};
    return result;
  }, [employeeShifts]);

  // Track when status and relevantShift are determined
  useEffect(() => {
    if (employeeShifts) {
      setIsStatusLoading(false);
    }
  }, [employeeShifts, status, relevantShift]);

  // Set button visibility based on status
  useEffect(() => {
    if (!status || !relevantShift) {
      setShowStartButton(false);
      setShowEndButton(false);
      setShowBreakButton(false);
      setShowEndMealBreakButton(false);
      setShowRestButton(false);
      setShowEndRestBreakButton(false);
      setCurrentBreak(null);
      return;
    }

    // Find active break from the relevant shift
    const activeBreak = relevantShift.breaks?.find(b => {
      return b.start && !b.end;
    }) || null;
    setCurrentBreak(activeBreak);

    const currentTime = new Date().getTime();

    switch (status) {
      case SHIFT_STATUS.IN_PROGRESS:
        setShowEndButton(true);
        setShowStartButton(false);
        // Show break buttons if shift is in progress
        setShowBreakButton(true);
        setShowRestButton(true);
        setShowEndMealBreakButton(false);
        setShowEndRestBreakButton(false);
        break;
      case SHIFT_STATUS.ON_BREAK:
        setShowEndButton(false);
        setShowStartButton(false);
        setShowBreakButton(false);
        setShowRestButton(false);
        setShowEndMealBreakButton(true);
        setShowEndRestBreakButton(false);
        break;
      case SHIFT_STATUS.ON_REST:
        setShowEndButton(false);
        setShowStartButton(false);
        setShowBreakButton(false);
        setShowRestButton(false);
        setShowEndMealBreakButton(false);
        setShowEndRestBreakButton(true);
        break;
      case SHIFT_STATUS.LATE:
        setShowStartButton(true);
        setShowEndButton(false);
        setShowBreakButton(false);
        setShowEndMealBreakButton(false);
        setShowRestButton(false);
        setShowEndRestBreakButton(false);
        break;
      case SHIFT_STATUS.SCHEDULED: {
        const shiftStartTime = new Date(relevantShift.start).getTime();
        const earlyClockInThreshold = shiftStartTime - EARLY_CLOCK_IN_WINDOW;
        setShowStartButton(currentTime >= earlyClockInThreshold);
        setShowEndButton(false);
        setShowBreakButton(false);
        setShowEndMealBreakButton(false);
        setShowRestButton(false);
        setShowEndRestBreakButton(false);
        break;
      }
      default:
        setShowStartButton(false);
        setShowEndButton(false);
        setShowBreakButton(false);
        setShowEndMealBreakButton(false);
        setShowRestButton(false);
        setShowEndRestBreakButton(false);
    }
  }, [status, relevantShift]);

  return {
    showStartButton,
    showEndButton,
    showBreakButton,
    showEndMealBreakButton,
    showRestButton,
    showEndRestBreakButton,
    status,
    relevantShift,
    isStatusLoading,
    currentBreak,
  };
};
